import {
  ItemListOptionMetadata,
  OrderGuideCollection,
  OrderGuidePartition,
} from '@/contexts/item-context-types';
import { ItemsContext_OrderGuideItemFieldsFragment } from '@/types';
import { useCallback, useMemo } from 'react';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';

type OrderGuideCollectionUUID = string;
type OrderGuidePartitionUUID = string;

interface OrderGuidePartitionToOrderGuideItemsMap {
  [
    orderGuidePartitionUUID: OrderGuidePartitionUUID
  ]: ItemsContext_OrderGuideItemFieldsFragment[];
}

export type OrderGuideSearchItemInfo = Record<
  OrderGuideCollectionUUID,
  OrderGuidePartitionToOrderGuideItemsMap
>;

// finds all order guide collections that a sku is in
export const getCollectionsForSku = (
  info: OrderGuideSearchItemInfo,
  sku: ItemListOptionMetadata,
  orderGuideCollections: OrderGuideCollection[],
): OrderGuideCollection[] => {
  return Object.keys(info)
    .filter((orderGuideCollectionUUID) => {
      const partitions = info[orderGuideCollectionUUID];

      return Object.values(partitions).some((orderGuideItems) => {
        return orderGuideItems.some(
          (orderGuideItem) =>
            orderGuideItem.variant_pack?.uuid === sku.variantPackUUID,
        );
      });
    })
    .map((orderGuideCollectionUUID) =>
      orderGuideCollections.find(
        (orderGuideCollection) =>
          orderGuideCollection.uuid === orderGuideCollectionUUID,
      ),
    )
    .filter(
      (collection): collection is OrderGuideCollection =>
        collection !== undefined,
    );
};

// finds all partitions (in a given order guide collection) that a sku is in
export const getPartitionsForSkuInCollection = (
  info: OrderGuideSearchItemInfo,
  sku: ItemListOptionMetadata,
  orderGuideCollectionUUID: OrderGuideCollectionUUID,
  orderGuideCollections: OrderGuideCollection[],
): {
  hasNotes: boolean;
  hasRequests: boolean;
  partition: OrderGuidePartition;
}[] => {
  const orderGuideCollection = orderGuideCollections.find(
    (collection) => collection.uuid === orderGuideCollectionUUID,
  );

  if (!orderGuideCollection || !info[orderGuideCollectionUUID]) {
    return [];
  }

  return (
    Object.keys(info[orderGuideCollectionUUID])
      .filter((partitionUUID) => {
        const orderGuideItems = info[orderGuideCollectionUUID][partitionUUID];
        return orderGuideItems.some(
          (orderGuideItem) =>
            orderGuideItem.variant_pack?.uuid === sku.variantPackUUID,
        );
      })
      .map((partitionUUID) => ({
        hasNotes: info[orderGuideCollectionUUID][partitionUUID]
          .filter(
            (orderGuideItem) =>
              orderGuideItem.variant_pack?.uuid === sku.variantPackUUID,
          )
          .some((item) => item.order_guide_restaurant?.memo),
        hasRequests: info[orderGuideCollectionUUID][partitionUUID]
          .filter(
            (orderGuideItem) =>
              orderGuideItem.variant_pack?.uuid === sku.variantPackUUID,
          )
          .some((item) => item.order_guide_restaurant?.request),
        partition: orderGuideCollection.partitions.find(
          (partition) => partition.uuid === partitionUUID,
        ),
      }))
      // return type of partition is OrderGuidePartition | undefined without this filter
      .filter(
        (
          entry,
        ): entry is {
          hasNotes: boolean;
          hasRequests: boolean;
          partition: OrderGuidePartition;
        } => entry.partition !== undefined,
      )
  );
};

// constructs the order guide info (collection, partition, and order guide item hierarchy) for a given item metadata
export const useSearchItemOrderGuideInfo = (): ((
  itemMetadata: ItemListOptionMetadata,
) => {
  info: OrderGuideSearchItemInfo;
  skusInOrderGuide: ItemListOptionMetadata[];
}) => {
  const { variantPackPartitionMap, collections } = useOrderGuideItemsContext();

  const partitionToOrderGuideMap = useMemo(() => {
    const map = new Map<OrderGuidePartitionUUID, OrderGuideCollectionUUID>();

    collections.forEach((collection) => {
      collection.partitions.forEach((partition) => {
        map.set(partition.uuid, collection.uuid);
      });
    });

    return map;
  }, [collections]);

  return useCallback(
    (itemMetadata: ItemListOptionMetadata) => {
      // determine which skus are in _any_ order guide
      const skusInOrderGuide = itemMetadata.skus.filter(
        (sku) => variantPackPartitionMap[sku.variantPackUUID],
      );

      const orderGuideSearchItemInfo: OrderGuideSearchItemInfo = {};

      // for each sku, find which order guides and partitions it is in
      skusInOrderGuide.forEach((sku) => {
        const variantPackPartitions =
          variantPackPartitionMap[sku.variantPackUUID];

        // for each partition the sku is in, add the order guide item to the map
        Object.entries(variantPackPartitions).forEach(
          ([orderGuidePartitionUUID, orderGuideItem]) => {
            const orderGuideCollectionUUID = partitionToOrderGuideMap.get(
              orderGuidePartitionUUID,
            );

            if (!orderGuideCollectionUUID) {
              return;
            }

            if (!orderGuideSearchItemInfo[orderGuideCollectionUUID]) {
              orderGuideSearchItemInfo[orderGuideCollectionUUID] = {};
            }

            if (
              !orderGuideSearchItemInfo[orderGuideCollectionUUID][
                orderGuidePartitionUUID
              ]
            ) {
              orderGuideSearchItemInfo[orderGuideCollectionUUID][
                orderGuidePartitionUUID
              ] = [];
            }

            orderGuideSearchItemInfo[orderGuideCollectionUUID][
              orderGuidePartitionUUID
            ].push(orderGuideItem);
          },
        );
      });

      return { info: orderGuideSearchItemInfo, skusInOrderGuide };
    },
    [variantPackPartitionMap, partitionToOrderGuideMap],
  );
};
